// @ts-strict-ignore
import {Component, Injector} from "@angular/core";
import {Translations} from "@translations";
import {CameraProxyType, DeviceType, SettingType, TransportProtocol, VideoSourceType, WebCameraView} from "@models";
import {SimCameraService, SimEnumExtendedService, SimMaterialDialogService, NotificationService, SimTrainingRoomService} from "@services";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";

export interface DialogData_VideoSourceConfigurationDialogInput extends MatDialogDataBaseInput {
	camera: WebCameraView;
}

export interface DialogData_VideoSourceConfigurationDialogOutput extends MatDialogDataBaseOutput {
	changedCamera?: WebCameraView;
}

@Component({
	selector: "sim-video-source-configuration-dialog",
	templateUrl: "./video-source-configuration-dialog.component.html",
	styleUrls: ["./video-source-configuration-dialog.component.scss"],
	standalone: false,
})
export class VideoSourceConfigurationDialogComponent extends BasicMaterialDialog<
	VideoSourceConfigurationDialogComponent,
	DialogData_VideoSourceConfigurationDialogInput,
	DialogData_VideoSourceConfigurationDialogOutput
> {
	T = Translations;
	DeviceType = DeviceType;
	SettingType = SettingType;
	CameraProxyType = CameraProxyType;
	cameraProxyTypes: {name: string; value: CameraProxyType}[] = null;
	backendProxyTypes: {name: string; value: VideoSourceType}[] = null;
	transportProtocols: {name: string; value: TransportProtocol}[] = null;
	public updateAllCamerasTask: Promise<any>;
	private doReload: boolean = false;

	constructor(
		_injector: Injector,
		protected _simCameraService: SimCameraService,
		protected _simEnumService: SimEnumExtendedService,
		protected _simTrainingRoomService: SimTrainingRoomService,
		protected _simNotificationService: NotificationService,
		protected _simMatDialogService: SimMaterialDialogService,
	) {
		super(_injector);

		this.initializeCameraProxyTypes();
		this.initializeTransportProtocols();
	}

	ngOnInit() {
		super.ngOnInit();
	}

	updateAllCameras(configurationPropertyName: string, configurationPropertyValue: any) {
		this._simMatDialogService
			.openDeleteDialog({
				deleteTitle: "Change all Camera Configurations",
				deleteText:
					"Do you really want to update all configurations for all cameras? " +
					"Please keep in mind that some device types like Epiphans and Wifi - Cameras may not support certain configurations like rotation, audio or compression.",
				deleteButtonText: "Yes",
				cancelText: "Cancel",
			})
			.then((data) => {
				if (data.action == "ok") {
					this._simCameraService
						.updateAllCameraConfigurations({
							configurationPropertyName,
							configurationPropertyValue,
						})
						.then(
							() => {
								this._simNotificationService.notifySuccess();
							},
							() => {
								this._simNotificationService.notifyError("Error updating Camera");
							},
						)
						.finally(() => {
							this.doReload = true;
						});
				}
			});
	}

	readonly axisTypes = [DeviceType.PTZCamera, DeviceType.Standard, DeviceType.ZoomCamera];

	canUpdateAllCamerasOfType(): boolean {
		if (this.axisTypes.includes(this.data.camera.deviceType)) return !!this.data.camera.configuration.type;
		else return !!this.data.camera.deviceType;
	}

	updateAllCamerasOfTypeText(): string {
		if (this.axisTypes.includes(this.data.camera.deviceType)) return this.data.camera.configuration.type;
		else return DeviceType[this.data.camera.deviceType];
	}

	updateAllCamerasOfType() {
		if (this.canUpdateAllCamerasOfType()) {
			this._simMatDialogService
				.openConfirmationDialog({
					title: "Apply settings to all cameras of same type",
					description: `Do you really want to apply these settings to all cameras of type "${this.updateAllCamerasOfTypeText()}"?`,
					okText: "Yes",
					cancelText: "Cancel",
				})
				.then((result) => {
					if (result.action == "ok") {
						this.updateAllCamerasTask = this._simCameraService
							.updateAllCamerasOfSameType({
								camera: this.data.camera,
							})
							.then(() => {
								this.doReload = true;
							});
					}
				});
		}
	}

	public restartCamera() {
		this._simMatDialogService
			.openConfirmationDialog({
				title: "Restart camera",
				description: `Do you really want to restart camera ${this.data.camera.name}?`,
				okText: "Yes",
				cancelText: "Cancel",
			})
			.then((result) => {
				if (result.action == "ok") {
					this.updateAllCamerasTask = this._simCameraService.restartCamera({
						cameraId: this.data.camera.id,
					});
				}
			});
	}

	public close() {
		if (this.doReload) {
			super.close({action: "ok", changedCamera: this.data.camera});
		} else {
			super.close({action: "cancel"});
		}
	}

	public ok() {
		this.doReload = true;
		this.close();
	}

	protected initializeTransportProtocols() {
		this.transportProtocols = [
			{name: "Udp", value: TransportProtocol.Udp},
			{name: "Tcp", value: TransportProtocol.Tcp},
		];
	}

	protected initializeCameraProxyTypes() {
		this.cameraProxyTypes = [
			{name: "NoProxy", value: CameraProxyType.NoProxy},
			{name: "RtspWebRtc", value: CameraProxyType.RtspWebRtc},
		];

		this.backendProxyTypes = [
			{name: "Default", value: VideoSourceType.Default},
			{name: "RTSP Server", value: VideoSourceType.RtspRestreamer},
		];
	}

	public getCameraReport_Url(cameraId: string): string {
		return this._simCameraService.getCameraReport_Url(cameraId);
	}
}
